import React, { useEffect, useRef } from 'react';

export const FractalFlame = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 400;
    canvas.height = 400;

    let time = 0;
    const particles = [];

    class FractalParticle {
      constructor(x, y, level) {
        this.x = x;
        this.y = y;
        this.level = level;
        this.life = 1.0;
        this.decay = 0.02 * (5 - level);
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = -Math.random() * 2 - 0.5;
        this.size = (5 - level) * 3;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.life -= this.decay;
        this.vy -= 0.05;
        this.vx *= 0.99;
      }

      draw(ctx) {
        if (this.life <= 0) return;
        
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size);
        
        if (this.level === 0) {
          gradient.addColorStop(0, `rgba(180, 200, 255, ${this.life * 0.8})`);
          gradient.addColorStop(0.2, `rgba(255, 255, 240, ${this.life * 0.6})`);
          gradient.addColorStop(0.5, `rgba(255, 240, 180, ${this.life * 0.4})`);
          gradient.addColorStop(1, `rgba(255, 200, 100, 0)`);
        } else if (this.level === 1) {
          gradient.addColorStop(0, `rgba(255, 255, 200, ${this.life * 0.7})`);
          gradient.addColorStop(0.3, `rgba(255, 220, 120, ${this.life * 0.5})`);
          gradient.addColorStop(0.7, `rgba(255, 180, 60, ${this.life * 0.3})`);
          gradient.addColorStop(1, `rgba(255, 120, 20, 0)`);
        } else {
          gradient.addColorStop(0, `rgba(255, 180, 60, ${this.life * 0.6})`);
          gradient.addColorStop(0.5, `rgba(255, 100, 20, ${this.life * 0.3})`);
          gradient.addColorStop(1, `rgba(200, 50, 10, 0)`);
        }
        
        ctx.fillStyle = gradient;
        ctx.fillRect(this.x - this.size, this.y - this.size, this.size * 2, this.size * 2);
      }
    }

    const ifs = (x, y, depth, maxDepth, scale) => {
      if (depth >= maxDepth) {
        particles.push(new FractalParticle(x, y, depth));
        return;
      }

      const transforms = [
        { dx: 0, dy: -20 * scale, scale: 0.8 },
        { dx: -15 * scale, dy: -10 * scale, scale: 0.6 },
        { dx: 15 * scale, dy: -10 * scale, scale: 0.6 },
      ];

      transforms.forEach(t => {
        if (Math.random() > 0.2) {
          const wobble = Math.sin(time * 3 + depth) * 2;
          ifs(x + t.dx + wobble, y + t.dy, depth + 1, maxDepth, scale * t.scale);
        }
      });
    };

    const drawWick = () => {
      const wickX = canvas.width / 2 - 1;
      const wickY = canvas.height - 20;
      
      ctx.fillStyle = '#333';
      ctx.fillRect(wickX, wickY, 2, 15);
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drawWick();

      const flicker = Math.sin(time * 5) * 5;
      ifs(canvas.width / 2 + flicker, canvas.height - 25, 0, 3, 1);

      particles.forEach((particle, index) => {
        particle.update();
        if (particle.life <= 0) {
          particles.splice(index, 1);
        }
      });

      ctx.globalCompositeOperation = 'screen';
      particles.forEach(particle => {
        particle.draw(ctx);
      });
      ctx.globalCompositeOperation = 'source-over';

      const glowRadius = 40 + Math.sin(time * 2) * 5;
      const glowGradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height - 25, 0,
        canvas.width / 2, canvas.height - 25, glowRadius
      );
      glowGradient.addColorStop(0, 'rgba(255, 200, 100, 0.1)');
      glowGradient.addColorStop(0.5, 'rgba(255, 150, 50, 0.05)');
      glowGradient.addColorStop(1, 'rgba(255, 100, 20, 0)');
      ctx.fillStyle = glowGradient;
      ctx.fillRect(
        canvas.width / 2 - glowRadius, 
        canvas.height - 25 - glowRadius, 
        glowRadius * 2, 
        glowRadius * 2
      );

      time += 0.02;
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100%',
      gap: '16px'
    }}>
      <canvas 
        ref={canvasRef} 
        style={{ 
          border: '1px solid var(--bw)',
          background: '#1a1a1a'
        }} 
      />
      <div style={{ textAlign: 'center' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700' }}>Fractal Flame</h3>
        <p style={{ margin: '0', opacity: '0.7', fontSize: '14px', fontFamily: 'Source Code Pro, monospace' }}>
          IFS-based particle system with realistic colors
        </p>
      </div>
    </div>
  );
};